/**
 * DynaMan's realized trades, drawn on the main price chart.
 *
 * <h2>Why a second module and not an argument to the first</h2>
 *
 * `integralDeltaTrades.js` already draws a backtest's trades, and this reuses its VISUAL language
 * wholesale — the same connector, the same entry triangle, the same hollow exit diamond, the same
 * outcome colours — so a reader who has learned one chart can read the other.
 *
 * What it does NOT reuse is the vocabulary. That file's `EXIT_LABELS` carries Integral Delta's
 * reasons (`INITIAL_STOP`, `TRENDLINE_EXIT`, `SR_TARGET`, …) and DynaMan's are a different set
 * from a different engine (`STATIC_STOP`, `TRAILING_FLOOR_BAILOUT`, `TRENDLINE_BREAK`). The two
 * even have near-homographs — `TRENDLINE_EXIT` and `TRENDLINE_BREAK` are different rules — and
 * that file's own note says every constant its engine can produce must be in its map, because a
 * missing key silently drops a whole exit category. Merging two engines' vocabularies into one map
 * is how that guarantee stops being checkable.
 *
 * <h2>THE POINT OF THIS FEATURE IS THE EXIT TIMEFRAME</h2>
 *
 * DynaMan's intraday bailout closes a premise on a 5m or 30m bar, hours before that session's
 * daily bar exists. On a daily chart that exit is drawn on the same candle an end-of-day exit
 * would be, so the marker ALONE cannot distinguish them — the timeframe has to be in the label.
 * Every exit tooltip leads with it, and an intraday exit is called out explicitly.
 *
 * <h2>Pure</h2>
 *
 * A simulation result and the chart's bars in, an overlay out. No network, no ECharts, no React.
 */

import { TRADE_COLORS, tradeLineStyle, outcomeColor } from './integralDeltaTrades';
import {
  decoratorLines, decoratorRichText, TRADE_HALF, cardSide, anchorPriceFor,
} from './tradeDecorator';

export const DYNAMAN_TRADES_OVERLAY_ID = 'dynaman-trades';

/**
 * Every exit reason the DynaMan evaluator can emit, in the order the state machine reaches them.
 *
 * <h2>A missing key is a silent gap, so the set is asserted</h2>
 *
 * `ExitReason` is a Java enum and this is a JavaScript object; nothing but a test connects them.
 * `dynaManTrades.test.js` pins the key set, because an exit rule added on the backend would
 * otherwise render here as a raw enum name — legible, but with no explanation of what the rule
 * actually was, on the one screen built to explain exactly that.
 */
export const DYNAMAN_EXIT_LABELS = {
  STATIC_STOP: 'the initial stop was touched',
  // The three ratcheted stops. Each says WHERE the level came from, because the distinction is
  // the whole point of the gates: a stop-out at the entry stop is a trade that never worked, at a
  // breakeven it is one that worked and gave it all back, and at the trail it is a winner that
  // stopped winning. Same event on the chart, three different findings.
  BREAKEVEN_STOP: 'stopped at breakeven — it earned its risk back, then gave it back',
  MFE_TRAIL: 'the excursion trail was touched — a multi-R winner handing part of it back',
  TRENDLINE_STOP: 'the rising swing trendline was touched (a resting stop, filled at the line)',
  CONFORMING_TRAIL: 'the conforming-bar trail was touched - a stop trailing the lows of green bars '
    + '(or the highs of red ones), which is the only exit a parabolic run with no pullback can '
    + 'offer (a resting stop, filled at the line)',
  // PROFIT PROTECTION. Deliberately worded apart from BREAKEVEN_STOP above it: that one is the
  // R-based gate moving the stop to the entry exactly, this one is the percentage lock placing
  // it a sliver beyond. They arm on different quantities and fire at different times on the same
  // trade, and one label for both would hide which rule actually protected it.
  BREAKEVEN_LOCK: 'the breakeven lock was touched — profit protection moved the stop to entry '
    + 'plus the configured offset once the trade was far enough in front, so it could no longer '
    + 'become a loss (a resting stop, filled at the level)',
  ACCELERATED_RATCHET: 'the accelerated ratchet was touched — past its activation the trail '
    + 'tightened to a fraction of the initial risk and hugged the favourable extreme '
    + '(a resting stop, filled at the level)',
  TRAILING_FLOOR_BAILOUT: 'closed through the trailing floor (20-bar high less 2 x ATR)',
  TRENDLINE_BREAK: 'closed through the swing trendline',
  // THE DIRECT ENGINE'S LINE, and the third of three. The wording names the mechanic rather than
  // the shape, because "trendline" alone is now ambiguous across three different rules.
  STEEP_TRENDLINE_BREAK: 'closed through the ratcheting steep trendline',
  // SUPERSEDED rather than exited: an opposing PTA threshold signal took the tranche's place on
  // its own bar. Its own label, because the trade did not fail -- it was replaced.
  PREEMPTED: 'superseded by an opposing threshold signal',
  // THE 1% HYSTERESIS STOP, closed through. The trade did not merely stop out -- the engine turned
  // round on the same bar, so the label says reversed rather than stopped.
  SAR_REVERSAL: 'closed beyond its 1% stop and reversed',
  // THE FLAT PERCENTAGE STOP that replaced it: closed through on the CLOSE, and the engine went
  // flat rather than turning round. Its own label, because "and reversed" is the difference.
  HARD_STOP: 'closed beyond its hard percentage stop',
  // ADVANCED TRADE MANAGEMENT. Three labels, and the first two are deliberately worded apart
  // from TRENDLINE_BREAK and TRENDLINE_STOP above them, which they would otherwise read as.
  // The fan is anchored at the ENTRY and steepens as the wave accelerates, so its break is a
  // statement about the rate of the advance rather than about a line through two swing pivots.
  TREND_BREAK: 'closed through the accelerating fan — a trendline anchored at the entry that '
    + 'steepened to each new swing pivot, so the break says the advance stopped accelerating '
    + '(an analytical exit, filled at the close)',
  // NOT a level at all, which is why it names the condition it fired on. The limit applies only
  // while breakeven is unreached, so a trade that earned its risk back is never timed out — and
  // a reader seeing this label knows the premise never got that far.
  TIME_STOP: 'the time limit expired before the trade reached breakeven — flattened at the close',
  ATR_TRAIL_STOP: 'the ATR trail was touched — a stop trailing the favourable extreme by a '
    + 'multiple of ATR, so it widens with volatility rather than with structure '
    + '(a resting stop, filled at the level)',
  // NOT a level being reached — the only exit that is a decision rather than a fill, and the only
  // one followed by an entry on the same candle. Worded so a reader does not go looking for the
  // line or the stop that caused it.
  CTS_REVERSAL: 'closed early — a new signal said the swing had turned, and took its place',
  // A resting LIMIT, not a stop: it fills at the target or BETTER on a gap. A trade that scaled
  // out at PTA and was then stopped reports the STOP, not this — what ENDED it is what is named.
  TARGET_PTB: 'ran to the second profit target (PTB)',
  // THE CTS EXIT SANDBOX. Five labels, one per mechanic, because the whole point of the sandbox
  // is that the philosophies are separately auditable — a run reporting every exit under one
  // name would answer none of the questions it was built to ask.
  // NOT 'breathable': with that switch off the level comes from the configured stop
  // strategy instead, and this label has to be true either way.
  CTS_SANDBOX_STOP: 'the resting stop was touched',
  CTS_TIME_EJECTOR: 'the time ejector fired — the snap-back did not arrive in time',
  CTS_RSI_EXIT: 'RSI reached the exit level — the snap-back had happened',
  CTS_MACD_EXIT: 'MACD crossed its signal against the trade',
  CTS_TRENDLINE_EXIT: 'price closed through the swing trendline, past its ATR margin',
  STILL_OPEN: 'still open — the data ended',
  UNCLASSIFIED: 'unclassified — the ledger has no branch for this exit',
};

/**
 * Runaway guard, not a display policy.
 *
 * <p>A full-history DynaMan run is 105 trades on SPY; the Integral Delta overlay carries thousands
 * at the same weight. This is set where something pathological would hang the tab rather than
 * where a normal run gets truncated — the sibling module's note records what a real cap cost when
 * it silently hid five sixths of a chart's history.
 */
export const MAX_DRAWN_TRADES = 10000;

/**
 * The SESSION DATE an ISO timestamp belongs to, as the chart spells its dates.
 *
 * <h2>String-sliced deliberately; `new Date(...)` here is a bug</h2>
 *
 * The engine emits an offset date-time in the EXCHANGE's own offset — `2020-02-20T11:35-05:00`.
 * The first ten characters are already the trading date as the exchange means it, which is exactly
 * what `barData.dates` holds.
 *
 * Parsing it into a `Date` and formatting back would convert through the VIEWER's timezone or
 * through UTC, and a 16:00 New York close is 21:00 UTC — the same day — while a 19:30 close is
 * 00:30 UTC the NEXT day. So a Date round-trip silently moves some exits onto the following
 * candle, and only for late sessions, which is the kind of off-by-one nobody finds by looking.
 */
export function sessionDateOf(isoTimestamp) {
  if (typeof isoTimestamp !== 'string' || isoTimestamp.length < 10) return null;
  const date = isoTimestamp.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
}

/** The clock time of an exit, for the tooltip. Null on a plain date with no time part. */
export function sessionTimeOf(isoTimestamp) {
  if (typeof isoTimestamp !== 'string') return null;
  const match = /^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2})/.exec(isoTimestamp);
  return match ? match[1] : null;
}

/** True when the exit came off an intraday series rather than the daily one. */
export function isIntradayExit(trade) {
  const tf = (trade && trade.exitTimeframe) || '';
  return tf !== '' && tf.toLowerCase() !== 'daily';
}

function fmt(value, places = 2) {
  return Number.isFinite(value) ? value.toFixed(places) : '—';
}

/** How an exit reads, falling back to the raw code rather than to nothing. */
export function describeExit(trade) {
  const code = trade && trade.exitReasonCode;
  return DYNAMAN_EXIT_LABELS[code] || trade?.exitReason || code || 'exit';
}

/**
 * The hover card for one trade.
 *
 * <p>The exit line leads with the TIMEFRAME because that is the claim being validated. An intraday
 * exit additionally says so in words: on a daily chart it sits on the same candle an end-of-day
 * exit would, so nothing else on screen distinguishes the two.
 */
export function describeDynaManTrade(trade) {
  const isLong = trade.direction === 'LONG';
  const exitTime = sessionTimeOf(trade.exitTime);
  const intraday = isIntradayExit(trade);

  return [
    `<b>#${trade.sequence} · ${isLong ? 'LONG' : 'SHORT'}${trade.open ? ' · OPEN' : ''}</b>`,
    `  entry ${sessionDateOf(trade.entryTime)} @ ${fmt(trade.entryPrice, 4)}`,
    `  exit  ${sessionDateOf(trade.exitTime)}${exitTime ? ` ${exitTime}` : ''} @ ${fmt(trade.exitPrice, 4)}`,
    // The two fields this overlay exists to show, on their own line.
    `  <b>${trade.exitTimeframe || '—'}</b> · ${trade.exitReasonCode || ''}`,
    `  ${describeExit(trade)}`,
    ...(intraday
      ? ['  <i>intraday bailout — risk cut before the session closed</i>']
      : []),
    `  ${fmt(trade.rMultiple)}R  ·  ${fmt(trade.pnlPercent)}%  ·  ${trade.barsHeld} bars`,
    ...(trade.open ? ['  (unresolved — the data ended, not the market)'] : []),
  ];
}

/**
 * Builds the overlay.
 *
 * @param result   the `/api/simulation/full` response — `{completedTrades: [...], openTrade}`
 * @param barData  the chart's loaded bars, for `dates`
 * @param opts.includeOpen  draw the open premise too. Default FALSE — it is a mark, not a result,
 *                          and a connector running to the last bar reads as a completed trade.
 */
export function buildDynaManTradeOverlay(result, barData, { limit = MAX_DRAWN_TRADES, includeOpen = false } = {}) {
  const dates = barData && Array.isArray(barData.dates) ? barData.dates : [];
  // `completedTrades` and `openTrade` are what BOTH run modes produce: the full endpoint returns
  // them and the stepped run's `complete` frame is assembled into the same shape. They are kept
  // separate on the wire precisely so the open premise cannot be counted by accident, which is
  // also why it is opt-in here rather than filtered out of a merged list.
  const completed = Array.isArray(result?.completedTrades) ? result.completedTrades : [];
  const openTrade = result?.openTrade || null;
  const all = openTrade ? [...completed, openTrade] : completed;
  // A Set, not indexOf per trade — the sibling module learned this at 1,568 trades against 11,500
  // dates, which is 36 million comparisons on the main thread per rebuild.
  const onChart = new Set(dates);

  /**
   * date -> the bar's own OHLC row, for HEMISPHERE ANCHORING.
   *
   * <p>A Map for the same reason `onChart` is a Set. Rows are ECharts candlestick order,
   * `[open, close, low, high]`, which `anchorPriceFor` indexes — stated here because the name
   * OHLC implies a different order and this repo has been caught by that twice.
   *
   * <p>Absent `ohlc` leaves the map empty rather than failing, and every card then falls back to
   * anchoring at its fill. That is the older callers' exact previous behaviour.
   */
  const barAt = new Map();
  if (Array.isArray(barData?.ohlc)) {
    dates.forEach((d, i) => { if (barData.ohlc[i]) barAt.set(d, barData.ohlc[i]); });
  }

  const realized = includeOpen ? all : completed;

  const withDates = realized.map(t => ({
    trade: t,
    entryDate: sessionDateOf(t.entryTime),
    exitDate: sessionDateOf(t.exitTime),
  }));

  const drawable = withDates.filter(({ trade, entryDate, exitDate }) =>
    entryDate && exitDate
    && onChart.has(entryDate) && onChart.has(exitDate)
    && Number.isFinite(trade.entryPrice) && Number.isFinite(trade.exitPrice));

  const shown = drawable.slice(-limit);

  // THE FIELD THE COLOUR HELPERS ACTUALLY READ IS `pnl`.
  //
  // This shim said `pctReturn` first — the name that appears in the sibling's tooltip code — and
  // every DynaMan trade drew RED, including a +582% winner, because `undefined > 0` is false.
  // Nothing failed: the overlay built, the markers appeared in the right places, and the only
  // symptom was a colour. Unit tests that assert structure cannot see it, which is why
  // `everyWinningTradeIsDrawnGreen` exists below.
  //
  // `pnl` here carries a PERCENTAGE, while the sibling study puts a per-unit currency figure in
  // the same field. Only its SIGN is read (`> 0`), so the two agree on what they use it for even
  // though they disagree on its units — worth knowing before anything else starts reading it.
  const asOutcome = t => ({ pnl: t.pnlPercent, reason: t.open ? 'END_OF_DATA' : t.exitReasonCode });

  /**
   * A STABLE IDENTITY for one trade, shared by its line halves and its two markers.
   *
   * <p>Declared here, above its first use. Written below the connectors first, which is a
   * temporal dead zone -- `const` is not hoisted, so the connector map threw before any of
   * this rendered. The same defect `App.tdz.test.js` exists for.
   */
  const keyOf = (trade, i) => (Number.isFinite(trade.sequence) ? `t${trade.sequence}` : `i${i}`);

  /**
   * ONE CARD, content and placement together.
   *
   * <p>Declared above its first use for the same reason `keyOf` is — `const` is not hoisted and
   * `connectors` runs immediately.
   *
   * <p>`price` is the FILL and stays on the card as the number a reader checks against the
   * ledger. `anchorPrice` is where the card HANGS, which is the bar's own extreme on the card's
   * hemisphere. They are separate fields because they answer different questions, and collapsing
   * them would put the bar's high on a card claiming to report an exit fill.
   */
  const decorator = (trade, half, date, price) => {
    const side = cardSide(trade, half);
    return {
      date,
      price,
      side,
      anchorPrice: anchorPriceFor(side, barAt.get(date), price),
      lines: decoratorLines(trade, half),
      // The rendered form. The plain `lines` stay beside it because they are what the tooltip and
      // the tests read -- rich markup is a rendering concern and asserting against
      // `{a|ENTRY (LONG)}` would test the syntax, not the card.
      rich: decoratorRichText(trade, half),
    };
  };

  const connectors = shown.map(({ trade, entryDate, exitDate }, i) => {
    const style = tradeLineStyle(asOutcome(trade));
    return {
      date1: entryDate,
      price1: trade.entryPrice,
      date2: exitDate,
      price2: trade.exitPrice,
      color: style.color,
      width: style.width,
      lineType: style.type,
      opacity: style.opacity,
      /**
       * A STABLE IDENTITY for this trade, so a toggled decorator survives a re-render.
       *
       * <p>`sequence` comes from the engine and is stable across runs of the same configuration;
       * the index is the fallback for a payload that has none. Without a key the visible-set would
       * be addressed by array position, and a re-run with one trade more would silently move every
       * open card onto a different trade.
       */
      tradeKey: keyOf(trade, i),
      /**
       * The card's content, precomputed.
       *
       * <p>Here rather than in the renderer because this is the one place that knows the trade's
       * own vocabulary -- its exit reasons, its timeframes, its direction. The renderer draws
       * whatever lines it is handed, which is what lets the sibling study reuse the same drawing
       * with a completely different exit vocabulary.
       */
      // EXACTLY TWO CARDS PER TRADE. There was a third, `BOTH`, carrying the entry and the exit
      // in one box -- and because it anchored at the entry coordinate it was drawn directly over
      // the entry card. 258 trades produced 774 cards, a third of them redundant and every one of
      // them an overlap. A short trade's single segment now opens these two; see `cardKeysFor`.
      decorators: {
        [TRADE_HALF.ENTRY]: decorator(trade, TRADE_HALF.ENTRY, entryDate, trade.entryPrice),
        [TRADE_HALF.EXIT]: decorator(trade, TRADE_HALF.EXIT, exitDate, trade.exitPrice),
      },
    };
  });

  const entries = shown.map(({ trade, entryDate }, i) => {
    const isLong = trade.direction === 'LONG';
    return {
      date: entryDate,
      price: trade.entryPrice,
      // CLICKING THE MARKER TOGGLES ITS OWN CARD, which is the affordance that works when the
      // whole trade is a pixel wide. Same key and the same vocabulary the line halves use, so the
      // click handler needs no second branch and the two routes cannot disagree about which card
      // they mean.
      tradeKey: keyOf(trade, i),
      half: TRADE_HALF.ENTRY,
      // The arrow points the way the trade was taken and sits on the side it points away from, so
      // it never covers the bar it is anchored to.
      position: isLong ? 'below' : 'above',
      symbol: 'triangle',
      symbolSize: 11,
      ...(isLong ? {} : { symbolRotate: 180 }),
      color: outcomeColor(asOutcome(trade)),
      tooltipLines: describeDynaManTrade(trade),
    };
  });

  const exits = shown.map(({ trade, exitDate }, i) => ({
    date: exitDate,
    price: trade.exitPrice,
    tradeKey: keyOf(trade, i),
    half: TRADE_HALF.EXIT,
    // A hollow diamond: distinct from the entry triangle at any zoom, and it does not point
    // anywhere — an exit has no direction and an arrow there would imply one.
    symbol: 'diamond',
    // An intraday exit is drawn LARGER. It lands on the same daily candle an end-of-day exit
    // would, so size is the only channel left that separates them at a glance; the tooltip
    // carries the actual claim.
    symbolSize: isIntradayExit(trade) ? 13 : 9,
    hollow: true,
    color: outcomeColor(asOutcome(trade)),
    tooltipLines: describeDynaManTrade(trade),
  }));

  return {
    id: DYNAMAN_TRADES_OVERLAY_ID,
    // The renderer's neutral trade-marker branch. Not 'integraldeltatrades' — same drawing, but
    // naming this overlay after a different study is how two features come to be assumed to be
    // one when somebody greps for the type.
    type: 'trademarkers',
    label: `DynaMan trades — ${result?.symbol || ''}`.trim(),
    connectors,
    entries,
    exits,
    /**
     * The trades actually DRAWN, for the yearly summary plaques.
     *
     * <p>`shown`, not the run's full list. A plaque reading "1995 +12.34%" over a chart that is
     * not showing those trades would be describing something the reader cannot check — and this
     * set is already filtered to trades whose dates exist on these bars. Where the two differ,
     * `countUndrawn` beside it is the number that says so.
     */
    trades: shown.map(({ trade }) => trade),
    drawnCount: shown.length,
    /** Off the result's own list, so a chart narrower than the run counts the difference too. */
    countUndrawn: realized.length - shown.length,
    intradayCount: shown.filter(({ trade }) => isIntradayExit(trade)).length,
    openExcluded: !includeOpen && openTrade !== null,
  };
}

/** The one-line status the chart shows when the overlay lands. */
export function dynaManOverlayStatus(overlay) {
  if (!overlay || overlay.drawnCount === 0) {
    // Said plainly rather than left as a chart that did not change. A run whose trades all fall
    // outside the loaded date range is the common case here, and it looks identical to a bug.
    return 'No DynaMan trades fall inside the loaded date range — nothing drawn.';
  }
  const parts = [`Drew ${overlay.drawnCount} DynaMan trade${overlay.drawnCount === 1 ? '' : 's'}`];
  if (overlay.intradayCount > 0) {
    parts.push(`${overlay.intradayCount} exited on an intraday bar`);
  }
  if (overlay.countUndrawn > 0) {
    parts.push(`${overlay.countUndrawn} outside the loaded range`);
  }
  if (overlay.openExcluded) {
    parts.push('the open premise is excluded');
  }
  return `${parts.join(' · ')}.`;
}

export { TRADE_COLORS };
